import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import DatePicker, { registerLocale } from 'react-datepicker'
import fi from 'date-fns/locale/fi'
import { ToggleButton } from '@material-ui/lab'
import { withStyles } from '@material-ui/core/styles'

import { d165, d255, d347, I24, II24, I25, II25, I26, II26 } from '../../constants/service'

import './SettingsPage.scss'
import 'react-datepicker/dist/react-datepicker.css'
import { useUserData } from '../../user/useUserData'
import { SEO } from '../../components/SEO'
import { BackButton } from '../../components/BackButton'
import { insignias } from '../../constants/insignias'
import { restaurants } from '../../constants/restaurantsData'

const StyledToggleButton = withStyles({
  root: {
    background: '#4b5320',
    border: 0,
    borderRadius: 0,
    color: 'white',
    height: 48,
  },
  selected: {
    border: 0,
    borderRadius: 3,
    boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .3)',
    color: 'white',
    height: 48,
    padding: '0 30px',
  },
  label: {
    color: 'white',
    textTransform: 'capitalize',
  },
})(ToggleButton)

export const SettingsPage = () => {
  const navigate = useNavigate()
  const {
    startDate,
    duration,
    isFemale,
    insigniaIndex,
    favoriteRestaurantUrl,
    saveStartDate,
    saveDuration,
    saveIsFemale,
    saveInsignia,
    saveFavoriteRestaurantUrl,
  } = useUserData()
  const hasPreferencesKey = 'hasPreferences'
  const hasPreferences = localStorage.getItem(hasPreferencesKey)

  useEffect(() => {
    registerLocale('fi', fi)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    saveStartDate(startDate)
    saveDuration(duration)
    saveIsFemale(isFemale)
    localStorage.setItem(hasPreferencesKey, 'true')
    void navigate('/')
  }

  return (
    <div className="settingsPage">
      <SEO
        title="Asetukset | Aamukasaaja"
        description="Tee Aamukasaajasta omasi: määritä palvelusjakso, arvomerkki ja suosikkiravintolasi."
        keywords="aamukasa, tj, laskuri, intti, asepalvelus"
        canonical="https://aamukasaaja.fi/asetukset"
      />
      <div className="header">
        {hasPreferences && <BackButton to="/info" />}
        <h2>Asetukset</h2>
      </div>
      <div className="batchContainer">
        <h3>Valitse saapumiserä</h3>
        <div className="columnsRow">
          <div className="column">
            <StyledToggleButton
              selected={startDate.toISOString() === I24.toISOString()}
              value={I24}
              type="button"
              onClick={() => saveStartDate(I24)}
            >
              I/24
            </StyledToggleButton>
            <StyledToggleButton
              selected={startDate.toISOString() === I25.toISOString()}
              value={I25}
              type="button"
              onClick={() => saveStartDate(I25)}
            >
              I/25
            </StyledToggleButton>
            <StyledToggleButton
              selected={startDate.toISOString() === I26.toISOString()}
              value={I26}
              type="button"
              onClick={() => saveStartDate(I26)}
            >
              I/26
            </StyledToggleButton>
          </div>
          <div className="column">
            <StyledToggleButton
              selected={startDate.toISOString() === II24.toISOString()}
              value={II24}
              type="button"
              onClick={() => saveStartDate(II24)}
            >
              II/24
            </StyledToggleButton>
            <StyledToggleButton
              selected={startDate.toISOString() === II25.toISOString()}
              value={II25}
              type="button"
              onClick={() => saveStartDate(II25)}
            >
              II/25
            </StyledToggleButton>
            <StyledToggleButton
              selected={startDate.toISOString() === II26.toISOString()}
              value={II26}
              type="button"
              onClick={() => saveStartDate(II26)}
            >
              II/26
            </StyledToggleButton>
          </div>
        </div>
      </div>
      <div className="durationContainer">
        <h3>Valitse palveluksen pituus</h3>
        <div className="durationColumn">
          <StyledToggleButton
            selected={duration === d165}
            value={d165}
            className="durationButton"
            type="button"
            onClick={() => saveDuration(d165)}
          >
            {d165.toString()}
          </StyledToggleButton>
          <StyledToggleButton
            selected={duration === d255}
            value={d255}
            className="durationButton"
            type="button"
            onClick={() => saveDuration(d255)}
          >
            {d255.toString()}
          </StyledToggleButton>
          <StyledToggleButton
            selected={duration === d347}
            value={d347}
            className="durationButton"
            type="button"
            onClick={() => saveDuration(d347)}
          >
            {d347.toString()}
          </StyledToggleButton>
        </div>
        <div className="femaleContainer">
          <label htmlFor="isFemale">
            <input
              id="isFemale"
              type="checkbox"
              className="femaleCheckbox"
              checked={isFemale}
              onChange={(event) => saveIsFemale(event.target.checked)}
            />
            Varusnaisen päiväraha
          </label>
        </div>
        <button className="submitButton" type="submit" onClick={handleSubmit}>
          Tallenna
        </button>
      </div>
      <div className="insigniaContainer">
        <h3>Valitse arvomerkki</h3>
        <div className="insigniaGrid">
          {insignias.map((src, index) => (
            <button
              key={index}
              type="button"
              className={`insigniaButton ${index === insigniaIndex ? 'selected' : ''}`}
              style={{ backgroundImage: `url(${src})` }}
              aria-label={`Arvomerkki ${index + 1}`}
              onClick={() => saveInsignia(index)}
            />
          ))}
        </div>
        <button className="submitButton" type="submit" onClick={handleSubmit}>
          Tallenna
        </button>
      </div>
      <div className="restaurantContainer">
        <h3>Valitse suosikkiravintola</h3>
        <div className="restaurantSelectWrapper">
          {(() => {
            const selectedUrl: string =
              typeof favoriteRestaurantUrl === 'string' ? favoriteRestaurantUrl : ''
            return (
              <select
                id="favoriteRestaurant"
                className="restaurantSelect"
                value={selectedUrl}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  (saveFavoriteRestaurantUrl as (url: string | null) => void)(
                    e.currentTarget.value || null
                  )
                }
              >
                <option value="">Ei valittu</option>
                {restaurants.map((r) => (
                  <option key={r.url} value={r.url}>
                    {r.name}
                  </option>
                ))}
              </select>
            )
          })()}
        </div>
        <button className="submitButton" type="submit" onClick={handleSubmit}>
          Tallenna
        </button>
      </div>
      <details className="customSettings">
        <summary>Valitse oma palvelusjakso</summary>
        <div className="settingsContainer">
          <form className="inputForm">
            <p>Aloituspäivämäärä</p>
            <DatePicker
              className="dateField"
              dateFormat="dd.MM.yyyy"
              onChange={(date) => saveStartDate(date)}
              onSelect={(date) => saveStartDate(date)}
              selected={startDate}
            />
            <p>Kesto</p>
            <input
              className="durationField"
              onChange={(event) => saveDuration(parseInt(event.target.value, 10))}
              type="number"
              value={duration}
            />
            <div className="femaleContainer">
              <label htmlFor="isFemale">
                <input
                  id="isFemale"
                  type="checkbox"
                  className="femaleCheckbox"
                  checked={isFemale}
                  onChange={(event) => saveIsFemale(event.target.checked)}
                />
                Varusnaisen päiväraha
              </label>
            </div>
            <button className="submitButton" type="submit" onClick={handleSubmit}>
              Tallenna
            </button>
          </form>
        </div>
      </details>
    </div>
  )
}
